import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Globe2, Lock, BookOpen, Clock, CheckCircle2, XCircle, AlertTriangle, ArrowRight, RotateCcw, FileText, BarChart3, LogOut, Video, Image, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const STUDENT_TOKEN_KEY = 'student_token';
const STUDENT_USER_KEY = 'student_user';

const api = (method, url, data) => {
  const token = localStorage.getItem(STUDENT_TOKEN_KEY);
  return axios({ method, url: `${API}${url}`, data, withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {} });
};

const CATEGORY_COLORS = {
  abituriyent: { primary: '#0077B6', label: 'Abituriyent hazırlığı' },
  miq: { primary: '#D32F2F', label: 'MİQ hazırlığı' },
  sertifikasiya: { primary: '#2E7D32', label: 'Sertifikasiya hazırlığı' },
};

export default function StudentCabinet() {
  // Initialize from localStorage immediately
  const [user, setUser] = useState(() => {
    try { const raw = localStorage.getItem(STUDENT_USER_KEY); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  const [loading, setLoading] = useState(() => !localStorage.getItem(STUDENT_TOKEN_KEY));
  const didCheck = useRef(false);

  useEffect(() => {
    if (didCheck.current) return;
    didCheck.current = true;
    const token = localStorage.getItem(STUDENT_TOKEN_KEY);
    if (!token) { setUser(false); setLoading(false); return; }
    axios.get(`${API}/api/auth/me`, { withCredentials: true, headers: { Authorization: `Bearer ${token}` } }).then(r => {
      if (r.data.role === 'student') {
        if (r.data.token) localStorage.setItem(STUDENT_TOKEN_KEY, r.data.token);
        localStorage.setItem(STUDENT_USER_KEY, JSON.stringify(r.data));
        setUser(r.data);
      } else { setUser(false); }
    }).catch((err) => {
      // Only clear on definitive 401, not network errors
      if (err.response?.status === 401) {
        localStorage.removeItem(STUDENT_TOKEN_KEY);
        localStorage.removeItem(STUDENT_USER_KEY);
        setUser(false);
      } else {
        // Network error - keep cached user, don't logout
        const cached = localStorage.getItem(STUDENT_USER_KEY);
        if (cached) { try { setUser(JSON.parse(cached)); } catch { setUser(false); } }
        else setUser(false);
      }
    }).finally(() => setLoading(false));
  }, []);

  // Auto-refresh token every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      const token = localStorage.getItem(STUDENT_TOKEN_KEY);
      if (token) {
        axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
          if (r.data.token) localStorage.setItem(STUDENT_TOKEN_KEY, r.data.token);
          if (r.data.role === 'student') localStorage.setItem(STUDENT_USER_KEY, JSON.stringify(r.data));
        }).catch(() => {});
      }
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <StudentLogin onLogin={setUser} />;
  return <StudentDashboard user={user} onLogout={() => setUser(false)} />;
}

function StudentLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const { data } = await api('post', '/api/auth/login', { email, password });
      if (data.otp_required) {
        setOtpStep(true); setOtpEmail(data.email); setCountdown(180);
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Giriş uğursuz oldu');
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i, v) => {
    if (!/^\d*$/.test(v)) return;
    const n = [...otp]; n[i] = v.slice(-1); setOtp(n);
    if (v && i < 5) otpRefs.current[i + 1]?.focus();
    if (n.every(d => d) && n.join('').length === 6) verifyOtp(n.join(''));
  };
  const handleOtpKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
  };
  const handleOtpPaste = (e) => {
    const p = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (p.length === 6) { setOtp(p.split('')); otpRefs.current[5]?.focus(); verifyOtp(p); }
  };

  const verifyOtp = async (code) => {
    setLoading(true); setError('');
    try {
      const { data } = await api('post', '/api/auth/verify-otp', { email: otpEmail, code });
      if (data.role === 'student') {
        if (data.token) localStorage.setItem(STUDENT_TOKEN_KEY, data.token);
        localStorage.setItem(STUDENT_USER_KEY, JSON.stringify(data));
        onLogin(data);
      } else setError('Bu hesab şagird hesabı deyil');
    } catch (err) {
      setError(err.response?.data?.detail || 'OTP doğrulama uğursuz');
      setOtp(['', '', '', '', '', '']); otpRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const resendOtp = async () => {
    try { await api('post', '/api/auth/resend-otp', { email: otpEmail }); setCountdown(180); setOtp(['', '', '', '', '', '']); otpRefs.current[0]?.focus(); }
    catch { setError('Yeni kod göndərilə bilmədi'); }
  };

  return (
    <div className="min-h-screen bg-[#03045E] flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="earth-globe-wrapper mx-auto mb-4">
            <div className="earth-globe-tilt">
              <div className="earth-globe-sphere w-14 h-14" style={{backgroundImage: `url(${API}/api/static/uploads/world_map.jpg)`}} />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">Şagird Kabineti</h1>
          <p className="font-body text-white/60 mt-2 text-sm">Dərsimiz Coğrafiya</p>
        </div>
        {!otpStep ? (
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-2xl" data-testid="student-login-form">
            <div className="flex items-center gap-2 mb-6">
              <Lock className="w-5 h-5 text-[#0077B6]" />
              <h2 className="font-body font-semibold text-[#1A2530]">Daxil olun</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="font-body text-sm text-[#5C6B7A] mb-1 block">E-poçt</label>
                <Input data-testid="student-email" type="email" value={email} onChange={e => setEmail(e.target.value)} required placeholder="email@example.com" />
              </div>
              <div>
                <label className="font-body text-sm text-[#5C6B7A] mb-1 block">Parol</label>
                <Input data-testid="student-password" type="password" value={password} onChange={e => setPassword(e.target.value)} required placeholder="Parolunuz" />
              </div>
            </div>
            {error && <p className="text-red-500 text-sm mt-4 font-body">{error}</p>}
            <Button data-testid="student-login-submit" type="submit" disabled={loading} className="w-full mt-6 bg-[#0077B6] hover:bg-[#005f92] font-body py-3 h-auto">
              {loading ? 'Yoxlanılır...' : 'Davam Et'}
            </Button>
          </form>
        ) : (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-8 shadow-2xl" data-testid="student-otp-form">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-5 h-5 text-[#0077B6]" />
              <h2 className="font-body font-semibold text-[#1A2530]">OTP Doğrulama</h2>
            </div>
            <p className="font-body text-sm text-[#5C6B7A] mb-6">6 rəqəmli kod müəllimə göndərildi.</p>
            <div className="flex justify-center gap-2 mb-4" onPaste={handleOtpPaste}>
              {otp.map((d, i) => (
                <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1}
                  value={d} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:border-[#0077B6] focus:ring-2 focus:ring-[#0077B6]/20 transition-all" />
              ))}
            </div>
            <div className="text-center mb-4">
              {countdown > 0 ? <span className="text-xs text-[#5C6B7A]">Kod etibarlıdır: <strong className="text-[#0077B6]">{Math.floor(countdown/60)}:{String(countdown%60).padStart(2,'0')}</strong></span>
              : <span className="text-xs text-red-500 font-semibold">Kodun müddəti bitdi</span>}
            </div>
            {error && <p className="text-red-500 text-sm text-center mb-4 font-body">{error}</p>}
            <Button onClick={() => verifyOtp(otp.join(''))} disabled={loading || otp.some(d => !d)} className="w-full bg-[#0077B6] hover:bg-[#005f92] font-body py-3 h-auto mb-3">
              {loading ? 'Doğrulanır...' : 'Doğrula'}
            </Button>
            <div className="flex items-center justify-between">
              <button onClick={() => { setOtpStep(false); setError(''); }} className="text-xs text-[#5C6B7A] hover:text-[#1A2530] font-body">Geriyə</button>
              <button onClick={resendOtp} disabled={countdown > 150} className="text-xs text-[#0077B6] hover:text-[#005f92] font-body disabled:opacity-40">Yeni kod göndər</button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}

function StudentDashboard({ user, onLogout }) {
  const [tab, setTab] = useState('assignments');
  const [assignments, setAssignments] = useState([]);
  const [results, setResults] = useState([]);
  const [fileAssignments, setFileAssignments] = useState([]);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = user?.token;
  const cc = CATEGORY_COLORS[user?.prep_category] || CATEGORY_COLORS.abituriyent;
  const personLabel = user?.prep_category === 'abituriyent' ? 'Şagird' : 'Tələbə';

  const fetchData = useCallback(async () => {
    try {
      const [a, r, f] = await Promise.all([
        api('get', '/api/student/assignments', null, token),
        api('get', '/api/student/results', null, token),
        api('get', '/api/student/files', null, token),
      ]);
      setAssignments(a.data);
      setResults(r.data);
      setFileAssignments(f.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => {
    await api('post', '/api/auth/logout');
    localStorage.removeItem(STUDENT_TOKEN_KEY);
    localStorage.removeItem(STUDENT_USER_KEY);
    onLogout();
  };

  if (activeQuiz) return <QuizTaker quiz={activeQuiz} token={token} onFinish={(result) => { setActiveQuiz(null); fetchData(); }} />;

  const pending = assignments.filter(a => !a.completed);
  const completed = assignments.filter(a => a.completed);

  return (
    <div className="min-h-screen bg-[#F4F7F6]" data-testid="student-dashboard">
      {/* Header */}
      <div className="text-white py-6" style={{ backgroundColor: cc.primary }}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="earth-globe-wrapper"><div className="earth-globe-tilt"><div className="earth-globe-sphere w-9 h-9" style={{backgroundImage: `url(${API}/api/static/uploads/world_map.jpg)`}} /></div></div>
            <div>
              <h1 className="text-xl font-bold">{personLabel} Kabineti</h1>
              <p className="font-body text-sm text-white/60">{user.name} ({user.email}) — {cc.label}</p>
            </div>
          </div>
          <Button data-testid="student-logout" variant="ghost" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/10">
            <LogOut className="w-4 h-4 mr-2" /> Çıxış
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-[#E2E8F0]">
            <div className="font-body text-sm text-[#5C6B7A]">Tapşırıqlar</div>
            <div className="text-2xl font-bold mt-1" style={{ color: cc.primary }}>{assignments.length}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-[#E2E8F0]">
            <div className="font-body text-sm text-[#5C6B7A]">Tamamlanmış</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{completed.length}</div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-[#E2E8F0]">
            <div className="font-body text-sm text-[#5C6B7A]">Gözləyən</div>
            <div className="text-2xl font-bold text-amber-600 mt-1">{pending.length}</div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-[#E2E8F0] mb-6">
            {[
              { value: 'assignments', icon: BookOpen, label: 'Tapşırıqlar' },
              { value: 'results', icon: BarChart3, label: 'Nəticələr' },
              { value: 'files', icon: FileText, label: 'Fayllar' },
            ].map(t => (
              <TabsTrigger key={t.value} value={t.value} className="font-body data-[state=active]:text-white" style={tab === t.value ? { backgroundColor: cc.primary } : {}}>
                <t.icon className="w-4 h-4 mr-2" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="assignments">
            {loading ? <div className="text-center py-12"><div className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: cc.primary, borderTopColor: 'transparent' }} /></div> :
            pending.length === 0 ? <div className="text-center py-12"><CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" /><p className="font-body text-[#5C6B7A]">Bütün tapşırıqlar tamamlanıb!</p></div> :
            <div className="space-y-4">
              {pending.map(a => (
                <motion.div key={a.id} className="bg-white rounded-xl p-6 border border-[#E2E8F0] card-hover" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid={`assignment-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-heading text-lg text-[#1A2530]">{a.quiz?.title || 'Tapşırıq'}</h3>
                      <div className="flex items-center gap-4 mt-2 font-body text-sm text-[#5C6B7A]">
                        <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {a.quiz?.time_limit || 30} dəq</span>
                        <span>{a.quiz?.quiz_type === 'pdf_test' ? `PDF | ${a.quiz?.question_count || 0} sual` : `${a.quiz?.questions?.length || 0} sual`}</span>
                        {a.quiz?.penalty_rule > 0 && <span className="text-amber-600">{a.quiz.penalty_rule} səhv = -1</span>}
                        {a.due_date && <span>Son tarix: {(() => { const d=new Date(a.due_date); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()}`; })()}</span>}
                      </div>
                    </div>
                    <Button data-testid={`start-assignment-${a.id}`} onClick={() => setActiveQuiz(a.quiz)} className="font-body" style={{ backgroundColor: cc.primary }}>
                      Başla <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>}

            {completed.length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading text-lg text-[#1A2530] mb-4">Tamamlanmış Tapşırıqlar</h3>
                <div className="space-y-3">
                  {completed.map(a => (
                    <div key={a.id} className="bg-white rounded-xl p-5 border border-green-200 bg-green-50/30" data-testid={`completed-${a.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-body font-semibold text-[#1A2530]">{a.quiz?.title}</h4>
                          <p className="font-body text-sm text-green-700 mt-1">
                            Nəticə: {a.attempt?.score}/{a.attempt?.total} (Düzgün: {a.attempt?.correct}, Səhv: {a.attempt?.wrong}, Cərimə: -{a.attempt?.penalty})
                          </p>
                        </div>
                        <CheckCircle2 className="w-6 h-6 text-green-500" />
                      </div>
                      {/* Explanation Materials */}
                      {a.quiz?.explanation_materials?.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <p className="font-body text-xs font-semibold text-[#1A2530] mb-2">İzahlı materiallar:</p>
                          <div className="flex flex-wrap gap-2">
                            {a.quiz.explanation_materials.map((m, i) => (
                              <a key={i} href={m.url} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0077B6]/10 text-[#0077B6] text-xs font-semibold font-body hover:bg-[#0077B6]/20 transition-colors">
                                {m.type === 'video' ? <Video className="w-3 h-3" /> : m.type === 'image' ? <Image className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                                {m.title || 'Material'}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      {a.quiz?.explanation_url && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <p className="font-body text-xs font-semibold text-[#1A2530] mb-2">İzah:</p>
                          {a.quiz.explanation_type === 'link' && a.quiz.explanation_url.includes('youtu') ? (
                            <div className="aspect-video rounded-lg overflow-hidden max-w-lg">
                              <iframe src={a.quiz.explanation_url.replace('watch?v=', 'embed/')} className="w-full h-full" allowFullScreen title="Explanation" />
                            </div>
                          ) : a.quiz.explanation_type === 'link' ? (
                            <a href={a.quiz.explanation_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0077B6]/10 text-[#0077B6] text-xs font-semibold font-body">
                              <FileText className="w-3 h-3" /> İzah linkini aç
                            </a>
                          ) : (
                            <ExplanationViewer quizId={a.quiz.id} type={a.quiz.explanation_type} viewLimit={a.quiz.explanation_view_limit} />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="results">
            {results.length === 0 ? <p className="text-center py-12 font-body text-[#5C6B7A]">Hələ nəticə yoxdur</p> :
            <div className="space-y-4">
              {results.map((r, i) => (
                <ResultCard key={r.id || i} result={r} index={i} />
              ))}
            </div>}
          </TabsContent>

          <TabsContent value="files">
            {fileAssignments.length === 0 ? (
              <p className="text-center py-12 font-body text-[#5C6B7A]">Sizə təyin olunmuş fayl yoxdur</p>
            ) : (
              <div className="space-y-3">
                {fileAssignments.map(f => (
                  <div key={f.id} className="bg-white rounded-xl p-5 border border-[#E2E8F0] flex items-center gap-4" data-testid={`student-file-${f.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-[#0077B6]/10 flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-[#0077B6]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-body font-semibold text-[#1A2530]">{f.file_name}</div>
                      <div className="font-body text-xs text-[#5C6B7A] mt-1">{f.file_path}</div>
                    </div>
                    <button onClick={async () => {
                      try { const { data } = await api('get', `/api/sharepoint/download/${f.file_id}`); if (data.download_url) window.open(data.download_url, '_blank'); }
                      catch { alert('Endirmə xətası'); }
                    }} className="px-4 py-2 rounded-lg bg-[#0077B6] text-white text-sm font-semibold hover:bg-[#005f92] transition-colors">
                      Endir
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function ExplanationViewer({ quizId, type, viewLimit }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [opened, setOpened] = useState(false);

  const openExplanation = async () => {
    if (blobUrl) { setOpened(true); return; }
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem(STUDENT_TOKEN_KEY);
      const resp = await fetch(`${API}/api/quiz-explanation/${quizId}?token=${token}`);
      if (resp.status === 403) { setError('Baxış limiti dolub'); setLoading(false); return; }
      if (!resp.ok) { setError('İzah yüklənmədi'); setLoading(false); return; }
      const blob = await resp.blob();
      setBlobUrl(URL.createObjectURL(blob));
      setOpened(true);
    } catch { setError('Xəta baş verdi'); }
    finally { setLoading(false); }
  };

  return (
    <div>
      {!opened ? (
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={openExplanation} disabled={loading} className="bg-[#0077B6] hover:bg-[#005f92] text-xs">
            {loading ? 'Yüklənir...' : type === 'video' ? 'Videonu aç' : 'PDF-i aç'}
          </Button>
          {viewLimit > 0 && <span className="text-xs text-[#5C6B7A]">Limit: {viewLimit} baxış</span>}
          {error && <span className="text-xs text-red-600">{error}</span>}
        </div>
      ) : blobUrl ? (
        <div>
          {type === 'video' ? (
            <div className="aspect-video rounded-lg overflow-hidden max-w-lg">
              <video src={blobUrl} controls controlsList="nodownload" className="w-full h-full" onContextMenu={e => e.preventDefault()} />
            </div>
          ) : (
            <div className="h-[500px] rounded-lg overflow-hidden border">
              <iframe src={`${blobUrl}#toolbar=0`} className="w-full h-full" title="Explanation" />
            </div>
          )}
          <Button size="sm" variant="ghost" onClick={() => setOpened(false)} className="mt-2 text-xs text-[#5C6B7A]">Gizlət</Button>
        </div>
      ) : null}
    </div>
  );
}


function ResultCard({ result: r, index: i }) {
  const [expanded, setExpanded] = useState(false);
  const pct = r.total > 0 ? Math.round((r.score / r.total) * 100) : 0;
  const dateStr = (() => { const d=new Date(r.submitted_at); return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; })();
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden" data-testid={`result-${r.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 text-left" data-testid={`result-toggle-${r.id}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-body font-semibold text-[#1A2530]">{r.quiz_title}</h4>
          <div className="flex items-center gap-3">
            <span className={`text-xl font-bold ${pct >= 70 ? 'text-green-600' : pct >= 40 ? 'text-amber-600' : 'text-red-600'}`}>{r.score}/{r.total}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${pct >= 70 ? 'bg-green-100 text-green-700' : pct >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{pct}%</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-[#5C6B7A]" /> : <ChevronDown className="w-4 h-4 text-[#5C6B7A]" />}
          </div>
        </div>
        <div className="flex items-center gap-4 font-body text-xs text-[#5C6B7A]">
          <span className="text-green-600 font-semibold">Düzgün: {r.correct}</span>
          <span className="text-red-600 font-semibold">Səhv: {r.wrong}</span>
          {r.penalty > 0 && <span className="text-amber-600">Cərimə: -{r.penalty}</span>}
          {r.time_spent > 0 && <span>{Math.floor(r.time_spent / 60)} dəq {r.time_spent % 60} san</span>}
          <span>{dateStr}</span>
        </div>
        <Progress value={pct} className="mt-3 h-2" />
      </button>
      {expanded && r.results?.length > 0 && (
        <div className="px-5 pb-5 border-t border-[#E2E8F0] pt-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {r.results.map((res, qi) => (
              <div key={qi} className={`flex items-center gap-2 p-2.5 rounded-lg text-sm ${res.is_open ? (res.is_correct === true ? 'bg-green-50 border border-green-200' : res.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200') : res.is_correct ? 'bg-green-50 border border-green-200' : res.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-white border">{qi + 1}</span>
                {res.is_open ? (
                  res.is_correct === true ? (
                    <><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /><span className="text-green-700 text-xs">{res.user_answer}</span></>
                  ) : res.is_correct === false ? (
                    <><XCircle className="w-4 h-4 text-red-600 flex-shrink-0" /><span className="text-red-700 text-xs">{res.user_answer || '-'}</span><span className="text-green-600 text-xs ml-auto">{res.correct_answer}</span></>
                  ) : (
                    <span className="text-blue-700 text-xs truncate flex-1">Açıq: {res.user_answer || '-'}</span>
                  )
                ) : res.is_correct ? (
                  <><CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /><span className="text-green-700 text-xs">{res.user_answer}</span></>
                ) : (
                  <><XCircle className="w-4 h-4 text-red-600 flex-shrink-0" /><span className="text-red-700 text-xs">{res.user_answer || '-'}</span><span className="text-green-600 text-xs ml-auto">{res.correct_answer}</span></>
                )}
              </div>
            ))}
          </div>
          {r.penalty > 0 && (
            <div className="mt-3 p-2 bg-amber-50 rounded-lg text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> {r.penalty_rule} səhv = -{r.penalty_deduction || 1} bal. Cəmi {r.penalty} bal cərimə.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function QuizTaker({ quiz, token, onFinish }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);
  const isPdfTest = quiz.quiz_type === 'pdf_test';
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // Start timer with persistence
  useEffect(() => {
    const startTimer = async () => {
      try {
        const { data } = await api('post', `/api/quizzes/${quiz.id}/start`);
        const startedAt = new Date(data.started_at);
        const elapsed = Math.floor((Date.now() - startedAt.getTime()) / 1000);
        const totalSeconds = (quiz.time_limit || 30) * 60;
        const remaining = Math.max(0, totalSeconds - elapsed);
        setTimeLeft(remaining);
      } catch { setTimeLeft((quiz.time_limit || 30) * 60); }
    };
    startTimer();
  }, [quiz.id, quiz.time_limit]);

  // Load PDF as blob for inline viewing (prevents CORS/download issues)
  useEffect(() => {
    if (!isPdfTest || !quiz.id) return;
    const loadPdf = async () => {
      try {
        const token = localStorage.getItem(STUDENT_TOKEN_KEY);
        const resp = await fetch(`${API}/api/quiz-pdf/${quiz.id}?token=${token}`);
        if (resp.ok) {
          const blob = await resp.blob();
          setPdfBlobUrl(URL.createObjectURL(blob));
        }
      } catch (err) { console.error('PDF load error:', err); }
    };
    loadPdf();
    return () => { if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); };
  }, [isPdfTest, quiz.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitQuiz = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const { data } = await api('post', `/api/quizzes/${quiz.id}/submit`, { answers }, token);
      setResult(data);
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  }, [quiz.id, answers, token, submitting]);

  useEffect(() => {
    if (result || timeLeft === null) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => { if (prev <= 1) { clearInterval(timerRef.current); submitQuiz(); return 0; } return prev - 1; });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [result, submitQuiz, timeLeft !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatTime = (s) => s === null ? '--:--' : `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const questions = quiz.questions || [];
  const currentQuestion = questions[currentQ];
  const questionCount = isPdfTest ? (quiz.question_count || 0) : questions.length;
  const answerCount = quiz.answer_count || 4;
  const openQs = new Set(quiz.open_questions || []);
  const answeredCount = Object.keys(answers).filter(k => answers[k]).length;

  if (timeLeft === null && !result) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin" /></div>;

  if (result) {
    return (
      <div className="min-h-screen bg-[#F4F7F6] py-16">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl p-8 md:p-12 border border-[#E2E8F0] shadow-lg" data-testid="quiz-result">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-[#1A2530] mb-4">Nəticə</h2>
              <div className="text-6xl font-bold text-[#0077B6]">{result.score}/{result.total}</div>
              <div className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-bold ${result.total > 0 && (result.score/result.total) >= 0.7 ? 'bg-green-100 text-green-700' : (result.score/result.total) >= 0.4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                {result.total > 0 ? Math.round((result.score / result.total) * 100) : 0}%
              </div>
              <p className="font-body text-[#5C6B7A] mt-4">Düzgün: {result.correct} | Yanlış: {result.wrong} | Cərimə: -{result.penalty} ({result.penalty_rule} səhv = -1 bal)</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
              {result.results?.map((r, i) => (
                <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${r.is_open ? (r.is_correct === true ? 'bg-green-50 border border-green-200' : r.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-blue-50 border border-blue-200') : r.is_correct ? 'bg-green-50 border border-green-200' : r.is_correct === false ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <span className="font-bold">{i+1}.</span>
                  {r.is_open ? (
                    r.is_correct === true ? <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /><span className="text-green-700">{r.user_answer}</span></> :
                    r.is_correct === false ? <><XCircle className="w-3.5 h-3.5 text-red-600" /><span className="text-red-700">{r.user_answer || '-'}</span><span className="text-green-600 ml-auto">{r.correct_answer}</span></> :
                    <span className="text-blue-700 truncate">{r.user_answer || '-'}</span>
                  ) :
                   r.is_correct ? <><CheckCircle2 className="w-3.5 h-3.5 text-green-600" /><span className="text-green-700">{r.user_answer}</span></> :
                   <><XCircle className="w-3.5 h-3.5 text-red-600" /><span className="text-red-700">{r.user_answer || '-'}</span><span className="text-green-600 ml-auto">{r.correct_answer}</span></>}
                </div>
              ))}
            </div>

            {quiz.explanation_url && (
              <div className="bg-[#F4F7F6] rounded-xl p-6 mb-6">
                <h3 className="font-heading text-lg text-[#1A2530] mb-3">İzah</h3>
                {quiz.explanation_type === 'link' && quiz.explanation_url.includes('youtu') ? (
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <iframe src={quiz.explanation_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')} className="w-full h-full" allowFullScreen title="Explanation" />
                  </div>
                ) : quiz.explanation_type === 'link' ? (
                  <a href={quiz.explanation_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#0077B6] font-semibold font-body text-sm">
                    <FileText className="w-4 h-4" /> İzah linkini aç
                  </a>
                ) : (
                  <ExplanationViewer quizId={quiz.id} type={quiz.explanation_type} viewLimit={quiz.explanation_view_limit} />
                )}
              </div>
            )}
            <Button data-testid="quiz-back" onClick={() => onFinish(result)} className="w-full bg-[#0077B6] hover:bg-[#005f92] font-body">
              Tapşırıqlara Qayıt
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // ========== PDF TEST MODE ==========
  if (isPdfTest) {
    return (
      <div className="min-h-screen bg-[#F4F7F6]" data-testid="pdf-test-view">
        {/* Header */}
        <div className="bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <h2 className="text-lg font-bold text-[#1A2530] truncate">{quiz.title}</h2>
          <div className="flex items-center gap-4">
            <span className="text-xs text-[#5C6B7A]">{answeredCount}/{questionCount} cavablanıb</span>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-body font-bold text-sm ${timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-[#0077B6]/10 text-[#0077B6]'}`}>
              <Clock className="w-3.5 h-3.5" /> {formatTime(timeLeft)}
            </div>
            <Button data-testid="pdf-quiz-submit" size="sm" onClick={submitQuiz} disabled={submitting} className="bg-[#03045E] hover:bg-[#03045E]/90 font-body">
              {submitting ? '...' : 'Bitir'}
            </Button>
          </div>
        </div>
        {/* Split Layout */}
        <div className="flex h-[calc(100vh-57px)]">
          {/* LEFT: PDF */}
          <div className="flex-1 border-r border-[#E2E8F0] relative" data-testid="pdf-viewer-panel" onContextMenu={e => e.preventDefault()}>
            {pdfBlobUrl ? (
              <iframe src={`${pdfBlobUrl}#toolbar=0&navpanes=0`} className="w-full h-full" title="PDF Test" />
            ) : (
              <div className="flex items-center justify-center h-full text-[#5C6B7A]">
                <div className="text-center">
                  <div className="w-8 h-8 border-4 border-[#0077B6] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm">PDF yüklənir...</p>
                </div>
              </div>
            )}
          </div>
          {/* RIGHT: Answer Card */}
          <div className="w-[380px] flex-shrink-0 overflow-y-auto bg-white p-4" data-testid="answer-card-panel">
            <h3 className="font-bold text-sm text-[#1A2530] mb-3">Cavab Kartı</h3>
            <div className="space-y-2">
              {Array.from({ length: questionCount }, (_, i) => {
                const qid = `q_${i + 1}`;
                const isOpen = openQs.has(i + 1);
                return (
                  <div key={i} className="flex items-start gap-2" data-testid={`pdf-q-${i+1}`}>
                    <span className="w-7 h-7 rounded-full bg-[#F4F7F6] flex items-center justify-center text-xs font-bold text-[#1A2530] flex-shrink-0 mt-0.5">{i + 1}</span>
                    {isOpen ? (
                      <input type="text" value={answers[qid] || ''} onChange={e => setAnswers(p => ({...p, [qid]: e.target.value}))}
                        placeholder="Cavabınızı yazın..." className="flex-1 h-8 rounded-md border border-[#E2E8F0] px-2 text-sm" data-testid={`pdf-open-${i+1}`} />
                    ) : (
                      <div className="flex gap-1 flex-1">
                        {Array.from({ length: answerCount }, (_, oi) => {
                          const letter = String.fromCharCode(65 + oi);
                          const selected = answers[qid] === letter;
                          return (
                            <button key={oi} onClick={() => setAnswers(p => ({...p, [qid]: letter}))} data-testid={`pdf-opt-${i+1}-${letter}`}
                              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${selected ? 'bg-[#0077B6] text-white shadow-md' : 'bg-[#F4F7F6] text-[#5C6B7A] hover:bg-[#E2E8F0]'}`}>
                              {letter}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== STANDARD TEST MODE ==========
  return (
    <div className="min-h-screen bg-[#F4F7F6] py-8">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-white rounded-2xl p-6 border border-[#E2E8F0] shadow-sm mb-6" data-testid="quiz-header">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1A2530]">{quiz.title}</h2>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-body font-bold text-sm ${timeLeft < 60 ? 'bg-red-100 text-red-700 timer-pulse' : 'bg-[#0077B6]/10 text-[#0077B6]'}`}>
              <Clock className="w-4 h-4" /> {formatTime(timeLeft)}
            </div>
          </div>
          <Progress value={((currentQ + 1) / questions.length) * 100} className="h-2" />
          <p className="font-body text-xs text-[#5C6B7A] mt-2">Sual {currentQ + 1} / {questions.length}</p>
        </div>

        {currentQuestion && (
          <motion.div key={currentQ} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl p-8 border border-[#E2E8F0] shadow-sm mb-6" data-testid="quiz-question">
            <p className="font-body text-lg font-semibold text-[#1A2530] mb-6">{currentQuestion.text}</p>
            <div className="space-y-3">
              {(currentQuestion.options || []).map((opt, oi) => (
                <button key={oi} data-testid={`quiz-option-${oi}`} onClick={() => setAnswers(p => ({ ...p, [currentQuestion.id]: opt }))}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all font-body text-sm ${answers[currentQuestion.id] === opt ? 'border-[#0077B6] bg-[#0077B6]/5 text-[#0077B6] font-semibold' : 'border-[#E2E8F0] hover:border-[#0077B6]/30 text-[#1A2530]'}`}>
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#F4F7F6] text-xs font-bold mr-3">{String.fromCharCode(65+oi)}</span>{opt}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <div className="flex gap-4">
          {currentQ > 0 && <Button variant="outline" data-testid="quiz-prev" onClick={() => setCurrentQ(p => p-1)} className="flex-1 font-body">Əvvəlki</Button>}
          {currentQ < questions.length - 1 ? (
            <Button data-testid="quiz-next" onClick={() => setCurrentQ(p => p+1)} className="flex-1 bg-[#0077B6] hover:bg-[#005f92] font-body">Növbəti</Button>
          ) : (
            <Button data-testid="quiz-submit" onClick={submitQuiz} disabled={submitting} className="flex-1 bg-[#03045E] hover:bg-[#03045E]/90 font-body">{submitting ? 'Göndərilir...' : 'Bitir və Göndər'}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
